import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { money, money0 } from '../lib/format';
import { SiteFooter } from '../components/Layout';
import { PayNow, type GuestPayment } from '../components/PayNow';
import { Empty, ErrorState, Loading, Modal, TextInput, useToast } from '../components/ui';
import {
  GuestPrivacyNotice,
  privacyPayload,
  useCollectionConfig,
  type ConsentChoices,
} from '../components/privacy/GuestPrivacyNotice';
import { GuestDataRequestLink } from '../components/privacy/GuestDataRequest';

interface GuestItem {
  id: string;
  name: string;
  price: number;
  wasPrice: number | null;
  isVegetarian: boolean;
  imageUrl: string | null;
}

interface GuestMenuData {
  property: { name: string; city: string; foodType: 'VEG' | 'NONVEG' | 'BOTH' };
  table: string | null;
  categories: Array<{ name: string; items: GuestItem[] }>;
}

type Diet = 'all' | 'veg' | 'nonveg';

interface GuestOrderResult {
  reference: string;
  total: number;
  items: number;
  payment: GuestPayment;
}

/**
 * What a guest sees after scanning the QR code on their table.
 *
 * Read-only and unauthenticated by design: no sign-in, no cart, no account.
 * Someone sitting down at a table wants to know what is on and what it costs,
 * on a phone, over a hotel wifi connection that may be poor - so this is one
 * request, no session, and the pictures are the only heavy thing on it.
 */
export function GuestMenu() {
  const { token = '' } = useParams();
  const [search] = useSearchParams();
  const table = search.get('table') ?? search.get('t') ?? '';
  const [diet, setDiet] = useState<Diet>('all');
  const [query, setQuery] = useState('');
  const [cart, setCart] = useState<Record<string, number>>({});
  const [checkout, setCheckout] = useState(false);
  const [placed, setPlaced] = useState<GuestOrderResult | null>(null);

  const menu = useQuery({
    queryKey: ['guest-menu', token, table],
    queryFn: () =>
      api.get<GuestMenuData>(
        `/api/public/menu/${token}${table ? `?table=${encodeURIComponent(table)}` : ''}`,
      ),
    retry: false,
  });

  const categories = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (menu.data?.categories ?? [])
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            (diet === 'all' ||
              (diet === 'veg' ? item.isVegetarian : !item.isVegetarian)) &&
            (term === '' || item.name.toLowerCase().includes(term)),
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [menu.data, diet, query]);

  const basketItems = useMemo(() => {
    const all = (menu.data?.categories ?? []).flatMap((c) => c.items);
    return Object.entries(cart)
      .filter(([, quantity]) => quantity > 0)
      .map(([id, quantity]) => ({ item: all.find((i) => i.id === id), quantity }))
      .filter((line): line is { item: GuestItem; quantity: number } => Boolean(line.item));
  }, [cart, menu.data]);
  const basketCount = basketItems.reduce((sum, line) => sum + line.quantity, 0);
  const basketTotal = basketItems.reduce((sum, line) => sum + line.item.price * line.quantity, 0);

  if (menu.isLoading) return <Loading label="Fetching the menu…" />;
  if (menu.error) {
    return (
      <div className="guest-shell">
        <div className="guest-body">
          <ErrorState error={menu.error} />
          <p className="small muted center" style={{ marginTop: 12 }}>
            Ask a member of staff for a printed menu.
          </p>
        </div>
      </div>
    );
  }

  const data = menu.data!;
  const total = data.categories.reduce((sum, category) => sum + category.items.length, 0);

  return (
    <div className="guest-shell">
      <header className="guest-head">
        <div className="brand-mark">S</div>
        <div>
          <h1>{data.property.name}</h1>
          <div className="small">
            {data.property.city}
            {data.table ? ` · Table ${data.table}` : ''}
          </div>
        </div>
        <Link className="btn sm" to={`/stay/${token}`}>
          Book a room
        </Link>
      </header>

      <div className="guest-body">
        {total === 0 ? (
          <Empty message="Nothing is on the menu just yet — please ask a member of staff." />
        ) : (
          <>
            <div className="guest-filters">
              <input
                className="input"
                placeholder="Search the menu"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {data.property.foodType === 'BOTH' ? (
                <div className="tabs">
                  {(
                    [
                      ['all', 'Everything'],
                      ['veg', 'Veg'],
                      ['nonveg', 'Non-veg'],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      className={`tab ${diet === id ? 'active' : ''}`}
                      onClick={() => setDiet(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {categories.length === 0 ? (
              <Empty message="Nothing matches that." />
            ) : (
              categories.map((category) => (
                <section key={category.name} className="guest-section">
                  <h2>{category.name}</h2>
                  <div className="guest-items">
                    {category.items.map((item) => (
                      <article key={item.id} className="guest-item">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt="" loading="lazy" />
                        ) : (
                          <div className="guest-item-blank" aria-hidden="true" />
                        )}
                        <div className="guest-item-text">
                          <div className="row gap-6">
                            <span className={`veg-dot ${item.isVegetarian ? '' : 'nonveg'}`} />
                            <span className="b">{item.name}</span>
                          </div>
                          <div className="guest-price">
                            {money0(item.price)}
                            {item.wasPrice ? (
                              <span className="guest-was">{money0(item.wasPrice)}</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="guest-add">
                          {(cart[item.id] ?? 0) === 0 ? (
                            <button
                              className="btn sm primary"
                              onClick={() => setCart((c) => ({ ...c, [item.id]: 1 }))}
                            >
                              Add
                            </button>
                          ) : (
                            <div className="row gap-6">
                              <button
                                className="btn ghost icon sm"
                                onClick={() =>
                                  setCart((c) => ({
                                    ...c,
                                    [item.id]: Math.max(0, (c[item.id] ?? 0) - 1),
                                  }))
                                }
                              >
                                &minus;
                              </button>
                              <span className="mono b">{cart[item.id]}</span>
                              <button
                                className="btn ghost icon sm"
                                onClick={() =>
                                  setCart((c) => ({ ...c, [item.id]: (c[item.id] ?? 0) + 1 }))
                                }
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ))
            )}

            <p className="small muted center" style={{ marginTop: 18 }}>
              Prices in ₹. Government taxes are charged as applicable. To order, please speak to a
              member of staff.
            </p>
          </>
        )}
      </div>

      {basketCount > 0 && !placed ? (
        <div className="guest-basket">
          <div>
            <b>
              {basketCount} item{basketCount === 1 ? '' : 's'}
            </b>
            <div className="xs">{money(basketTotal)} plus taxes</div>
          </div>
          <button className="btn primary" onClick={() => setCheckout(true)}>
            Review order
          </button>
        </div>
      ) : null}

      {checkout ? (
        <GuestCheckout
          token={token}
          table={data.table}
          items={basketItems}
          total={basketTotal}
          onClose={() => setCheckout(false)}
          onPlaced={(result) => {
            setCheckout(false);
            setCart({});
            setPlaced(result);
          }}
        />
      ) : null}

      {placed ? (
        <Modal title="Order placed" onClose={() => setPlaced(null)}>
          <div className="col gap-16" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40 }}>&#10003;</div>
            <div>
              <b>The kitchen has your order.</b>
              <div className="small muted" style={{ marginTop: 6 }}>
                Quote <b className="mono">{placed.reference}</b> if you need to ask about it.
              </div>
            </div>
            <div className="small">
              Total <b>{money(placed.total)}</b>
            </div>
            <PayNow
              payment={placed.payment}
              name={data.property.name}
              description={`Order ${placed.reference}`}
              fallback="A member of staff will confirm it with you and take payment."
            />
          </div>
        </Modal>
      ) : null}

      {token ? (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <GuestDataRequestLink token={token} />
        </div>
      ) : null}
      <SiteFooter />
    </div>
  );
}

/**
 * The last step before a diner commits.
 *
 * No account and no card. This puts the order in front of the kitchen and
 * leaves paying to the staff, which is how a restaurant floor already works -
 * and it means this page never holds anybody payment details.
 */
function GuestCheckout({
  token,
  table,
  items,
  total,
  onClose,
  onPlaced,
}: {
  token: string;
  table: string | null;
  items: Array<{ item: GuestItem; quantity: number }>;
  total: number;
  onClose: () => void;
  onPlaced: (result: GuestOrderResult) => void;
}) {
  const { fail } = useToast();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const collection = useCollectionConfig(token);
  const [consentChoices, setConsentChoices] = useState<ConsentChoices>({});

  const place = useMutation({
    mutationFn: () =>
      api.post<GuestOrderResult>(`/api/public/menu/${token}/orders`, {
        customerName: name.trim(),
        customerPhone: phone.trim() || undefined,
        table: table ?? undefined,
        note: note.trim() || undefined,
        lines: items.map(({ item, quantity }) => ({ menuItemId: item.id, quantity })),
        privacy: privacyPayload(collection.data, consentChoices),
      }),
    onSuccess: onPlaced,
    onError: fail,
  });

  return (
    <Modal
      title="Your order"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Keep looking
          </button>
          <button
            className="btn primary"
            disabled={!name.trim() || place.isPending}
            onClick={() => place.mutate()}
          >
            {place.isPending ? 'Sending…' : 'Place the order'}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="col gap-6">
          {items.map(({ item, quantity }) => (
            <div key={item.id} className="row between">
              <span>
                {item.name} <span className="muted">&times;{quantity}</span>
              </span>
              <span>{money(item.price * quantity)}</span>
            </div>
          ))}
          <div className="row between" style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}>
            <b>Total</b>
            <b>{money(total)}</b>
          </div>
          <div className="xs muted">Government taxes are added on the bill.</div>
        </div>

        <div className="form-grid">
          <TextInput
            className="span2"
            label="Your name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextInput
            label="Phone"
            hint="So staff can find you"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <TextInput
            label="Anything to tell the kitchen?"
            placeholder="Less spicy, no onion"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {table ? <p className="small muted">Serving to table {table}.</p> : null}
        <GuestPrivacyNotice
          config={collection.data}
          choices={consentChoices}
          onChange={setConsentChoices}
        />
      </div>
    </Modal>
  );
}
